import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import {
  createSearchState,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  formListValueFormatter,
  FormSearchResultAdvanced,
  objectToCSVByKey,
  useDataProvider,
  useLocalization,
  useMasterData,
  useSnackbar,
  useStore,
  useUserAccess,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import {
  IconLibrary,
  StackablePageButtons,
  FormFields,
  PageSection,
  getActiveInactiveStatus,
  getDefaultValueByControl,
  ArrowTabGroup,
  ArrowTab,
  CustomTabPanel,
} from '@product-framework/index';
import {
  FORM_VIEW,
  LOCATIONS_FORM_VIEW,
} from '@product-framework/pages/ServiceProvider/constants';
import { hasVisibleLocationSystemSettingsTabs } from '@product-framework/pages/ServiceProvider/scope';
import { useServiceModule } from '@product-framework/components/ServiceModuleProvider';
import { useNavigate, useParams } from 'react-router-dom';
import { ServiceProviderLocationNumberSearchDisplay } from '@product-framework/components/FormFields';
import {
  id,
  serviceProviderId,
  createdByUserId,
  lastModifiedByUserId,
  locationName,
  locationNumber,
  isDefaultLocation,
  status,
} from './accessorFunctions';
import listSchema from './list.json';
import searchSchema from './search.json';
import validationSchema from './validations';
import ServiceProviderLocationCreate from '../create';
import { useServiceProviderLocation } from '../components/ServiceProviderLocationProvider';
import LocationStepperConfig from './LocationStepperTabsConfig';
import FooterNavigation from '../../common/FooterNavigation';

function ServiceProviderLocationList({
  serviceProviderId: pageId,
  tabErrors,
  action,
  entityId,
  currentIndex,
  spDispatchMethod,
}) {
  const { formatMessage, messages } = useLocalization();
  const navigate = useNavigate();
  const store = useStore();
  const snackbar = useSnackbar();
  const masterData = useMasterData();
  const { moduleSettings = {} } = useServiceModule();
  const { dispatchPurchased, claimsPurchased } = moduleSettings;
  const [resultsFound, setResultsFound] = useState(0);
  const [stackablePageButtons, setStackablePageButtons] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const { hasInvalidLocationAddress = [] } = tabErrors;
  const spLocationContext = useServiceProviderLocation() || {};
  const userAccess = useUserAccess();

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);

  const listApiUrl = `${dataProvider.getApiUrl()}/sxcl/v1/service-providers/${pageId}/locations`;

  const { tabNumber, subTabNumber } = useParams();

  const serviceAreaEnabled =
    masterData?.applicationFeatureDemo?.serviceArea || false;

  const dispatchCalendarEnabled =
    masterData?.applicationFeatureDemo?.dispatchCalendar || false;

  const hasAvailabilityFeature =
    dispatchCalendarEnabled &&
    masterData.dispatchMethods.find(
      i => i.value === 'PRESET_CALENDAR_BASED_SCHEDULING' && i.status === true,
    ) &&
    spDispatchMethod === 'PRESET_CALENDAR_BASED_SCHEDULING';

  const hasSystemSettings =
    hasVisibleLocationSystemSettingsTabs(moduleSettings);

  const stepperTabs = useMemo(
    () =>
      LocationStepperConfig({
        userAccess,
        serviceAreaEnabled,
        formatMessage,
        hasAvailabilityFeature,
        hasSystemSettings,
        dispatchPurchased,
        claimsPurchased,
      }),
    [userAccess, serviceAreaEnabled, hasAvailabilityFeature, hasSystemSettings],
  );

  const parsedSubTabNumber = parseInt(subTabNumber ?? 0, 10);
  const currentTabValue =
    parsedSubTabNumber <= stepperTabs.length ? parsedSubTabNumber - 1 : 0;

  const createButton = {
    label: formatMessage({
      id: 'CREATE_NEW_SERVICEPROVIDERLOCATION_STACKABLE_BUTTON',
      defaultMessage: 'Create Service Provider Location',
    }),
    permissionName: 'CAN_CREATE_SP_LOCATIONS',
    Icon: IconLibrary.ServiceProviderLocation,
    variant: 'outlined',
    onClick: () => {
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.CREATE
        }`,
      );
    },
  };

  const listButton = {
    label: formatMessage({
      id: 'SHOW_SERVICEPROVIDERLOCATION_LIST_STACKABLE_BUTTON',
      defaultMessage: 'List Service Provider Locations',
    }),
    permissionName: 'CAN_LIST_SP_LOCATIONS',
    Icon: IconLibrary.ServiceProviderLocation,
    variant: 'outlined',
    onClick: () => {
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.LIST
        }`,
      );
    },
  };

  const searchButton = {
    label: !showSearch
      ? formatMessage({
          id: 'SHOW_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Search',
        })
      : formatMessage({
          id: 'HIDE_SEARCH_STACKABLE_BUTTON',
          defaultMessage: 'Hide Search',
        }),
    Icon: !showSearch ? IconLibrary.Search : IconLibrary.HideSearch,
    variant: 'contained',
    permissionName: 'CAN_SEARCH_SP_LOCATIONS',
    onClick: () => {
      setShowSearch(!showSearch);
    },
  };

  const issueButton = {
    label: formatMessage(
      {
        id: 'ISSUE_STACKABLE_BUTTON',
        defaultMessage: '{count} issue{plural}',
      },
      {
        count: hasInvalidLocationAddress?.length,
        plural: hasInvalidLocationAddress?.length > 1 ? 's' : '',
      },
    ),
    Icon: FmdBadIcon,
    color: 'error',
    variant: 'contained',
    permissionName: 'CAN_SEARCH_SP_LOCATIONS',
    onClick: () => {
      const locationNumberString = objectToCSVByKey(
        hasInvalidLocationAddress,
        'locationNumber',
      );
      const filters = [
        {
          field: 'locationNumber',
          operator: 'in',
          value: locationNumberString,
        },
      ];
      // ServiceProviderLocationList is store name from locations list page
      store.domain.list.set('ServiceProviderLocationList', { filters });
      navigate(
        `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
          FORM_VIEW.LIST
        }`,
      );
    },
  };

  const preProcessSearchRequestData = data => {
    const { id: idArg, locationNumber: locationNumberArg, ...other } = data;

    const formattedLocationNumber = Array.isArray(locationNumberArg)
      ? objectToCSVByKey(locationNumberArg, 'value')
      : null;

    return Object.fromEntries(
      Object.entries({
        ...(formattedLocationNumber && {
          locationNumber: formattedLocationNumber,
        }),
        ...other,
      }).filter(
        ([, value]) => value !== undefined && value !== null && value !== '',
      ),
    );
  };

  const submitSearchForm = async data => {
    const requestData = preProcessSearchRequestData(data);
    const searchState = createSearchState(requestData, searchSchema);
    store.domain.list.set('ServiceProviderLocationList', searchState);
  };

  const preProcessSearchValues = async searchedParams => {
    const formatterMap = {
      isDefaultLocation: value => (value === 'true' ? 'Yes' : 'No'),
      status: getActiveInactiveStatus,
      locationNumber: async value =>
        ServiceProviderLocationNumberSearchDisplay(
          value,
          pageId,
          dataProvider,
          snackbar,
        ),
    };

    return formListValueFormatter({ data: searchedParams, formatterMap });
  };

  const navigateLocationUrl = (recordId, subTab) => {
    return `/serviceproviders/edit/${pageId}/${currentIndex + 1}/${
      FORM_VIEW.EDIT
    }/${recordId}/${subTab}`;
  };

  const listProps = {
    columnPrimaryId: 'id',
    storeName: 'ServiceProviderLocationList',
    dataProvider,
    schema: listSchema,
    initialState: {
      showColumnFilters: false,
    },
    downloadApiUrl: null,
    listApiUrl,
    accessorFunctions: {
      id,
      serviceProviderId,
      createdByUserId,
      lastModifiedByUserId,
      locationName,
      locationNumber,
      isDefaultLocation,
      status,
    },
    accessorFunctionsAdditional: {
      navigate: recordId =>
        navigateLocationUrl(recordId, LOCATIONS_FORM_VIEW.DETAILS),
    },
    onRowCountChange: rowCount => {
      setResultsFound(rowCount);
    },
    sortOrder: 'ASC',
    sortBy: 'locationName',
  };

  const searchProps = {
    customComponents: FormFields,
    IconLibrary,
    schema: searchSchema,
    storeName: listProps.storeName,
    onSubmit: submitSearchForm,
    onClear: () => {
      store.domain.list.reset(listProps.storeName);
    },
    validationSchema: validationSchema(formatMessage),
    hideSearchForm: () => {
      setShowSearch(false);
    },
    initialValues: {
      serviceProviderId: pageId,
    },
    getValueByControl: getDefaultValueByControl,
  };

  const searchParamsProps = {
    storeName: listProps.storeName,
    preProcessSearchValues,
    schema: searchSchema,
    resultsFound,
  };

  useEffect(() => {
    const buttons = [];
    if (
      issueButton &&
      hasInvalidLocationAddress?.length > 0 &&
      action !== FORM_VIEW.EDIT
    ) {
      buttons.push(issueButton);
    }
    if (action === FORM_VIEW.CREATE) {
      buttons.push(listButton);
      store.domain.list.reset('ServiceProviderLocationList');
    } else if (action === FORM_VIEW.LIST) {
      buttons.push(createButton);
      buttons.push(searchButton);
    } else if (action === FORM_VIEW.EDIT) {
      buttons.push(createButton);
      buttons.push(listButton);
      store.domain.list.reset('ServiceProviderLocationList');
    }
    if (action !== FORM_VIEW.LIST) {
      setShowSearch(false);
    }

    setStackablePageButtons(buttons);
  }, [action, showSearch, tabErrors, pageId, entityId, currentIndex, messages]);

  const tabs = useMemo(() => {
    const maxTabs = stepperTabs.length;
    return stepperTabs.map((c, i) => {
      return (
        <ArrowTab
          inner
          key={c.label}
          {...(i === 0 && { arrowHeadArea: 90 })}
          {...(i !== 0 && { arrowBackArea: 5 })}
          maxTabs={maxTabs}
          label={c?.label}
          errorCount={c.getErrorCount?.(tabErrors) ?? null}
          config={c}
        />
      );
    });
  }, [stepperTabs, tabErrors, messages]);

  const onMainTabChange = useCallback(
    newValue => {
      navigate(
        `/serviceproviders/edit/${pageId}/${tabNumber}/edit/${entityId}/${newValue + 1}`,
      );
    },
    [navigate, pageId, entityId, tabNumber],
  );

  const ActiveStepper = stepperTabs[currentTabValue];

  return (
    <PageSection
      HeaderContent={
        <StackablePageButtons
          backButton={false}
          buttons={stackablePageButtons}
        />
      }
      label={formatMessage(
        {
          id: 'SERVICE_PROVIDER_LOCATION_EDIT_HEADING',
          defaultMessage: 'Service Provider Location {locationName}',
        },
        {
          locationName: spLocationContext?.spLocation?.locationName
            ? ` - ${spLocationContext?.spLocation?.locationName}`
            : '',
        },
      )}>
      <Grid2 container direction="column" spacing={2}>
        <Grid2 xs={12}>
          <FormSearchResultAdvanced {...searchParamsProps} />
        </Grid2>

        {action === FORM_VIEW.CREATE && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICEPROVIDERLOCATION_CREATE_NEW_HEADING',
              defaultMessage: 'Create New Location',
            })}>
            <ServiceProviderLocationCreate
              serviceProviderId={pageId}
              currentIndex={currentIndex}
            />
          </PageSection>
        )}

        {action === FORM_VIEW.EDIT && (
          <Box>
            <Box
              sx={{
                width: {
                  xs: '100%',
                  sm: '100%',
                },
              }}>
              <ArrowTabGroup
                inner
                value={currentTabValue}
                onChange={(event, newValue) => {
                  onMainTabChange(Number(newValue));
                }}>
                {tabs}
              </ArrowTabGroup>
            </Box>
            <Box>
              <CustomTabPanel
                inner
                value={currentTabValue}
                index={currentTabValue}>
                <ActiveStepper.Component
                  setTabValue={tabNo => onMainTabChange(tabNo)}
                  serviceProviderId={pageId}
                  currentIndex={currentTabValue}
                  stepperTabs={stepperTabs}
                  tabErrors={tabErrors}
                  onChange={() => {
                    // checkAndSetSPProfile();
                  }}
                  action={action}
                  entityId={entityId}
                  subSectionType="LOCATION"
                />

                <FooterNavigation
                  inner
                  onChange={newValue => {
                    onMainTabChange(newValue);
                  }}
                  currentIndex={currentTabValue}
                  stepperTabs={stepperTabs}
                />
              </CustomTabPanel>
            </Box>
          </Box>
        )}

        {action === FORM_VIEW.LIST && showSearch && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICEPROVIDERLOCATION_SEARCH_HEADING',
              defaultMessage: 'Search Locations',
            })}>
            <Box>
              <CRUDComponent
                type={CRUD_COMPONENT_TYPE.SEARCH_ADVANCED}
                searchProps={searchProps}
              />
            </Box>
          </PageSection>
        )}

        {action === FORM_VIEW.LIST && (
          <PageSection
            subSection
            containerProps={{ sx: { mb: 3 } }}
            label={formatMessage({
              id: 'SERVICEPROVIDERLOCATION_SEARCH_HEADING',
              defaultMessage: 'Locations List',
            })}>
            <CRUDComponent
              type={CRUD_COMPONENT_TYPE.ADVANCED_LIST}
              listProps={listProps}
            />
          </PageSection>
        )}
      </Grid2>
    </PageSection>
  );
}

export default observer(ServiceProviderLocationList);
